package com.schoolconnect.app.feature.subjects

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.list.AdminListMessageAndFilters
import com.schoolconnect.app.designsystem.list.AdminListPageScaffold
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListTableShimmer
import com.schoolconnect.app.designsystem.list.AdminListToolbar
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.designsystem.AppLabeledDropdownField
import com.schoolconnect.app.feature.common.toWindow
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.filter
import ischoolconnect.sharedui.generated.resources.plus
import org.jetbrains.compose.resources.painterResource

@Composable
fun SubjectsScreen(
    state: SubjectsUiState,
    onEvent: (SubjectsUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {},
    headerActions: @Composable RowScope.() -> Unit = {}
) {
    val pageItems = state.subjects
    val pageWindow = state.paging.toWindow(pageItems.size)
    var openActionMenuSubjectId by remember { mutableStateOf<String?>(null) }
    var rowsPerPageExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(pageItems) {
        if (openActionMenuSubjectId != null && pageItems.none { it.id == openActionMenuSubjectId }) {
            openActionMenuSubjectId = null
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(SubjectsBackground)
    ) {
        AdminListPageScaffold(
            title = "Manage Subjects",
            subtitle = "Create, update and manage subject master records.",
            backgroundColor = SubjectsBackground,
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            maxContentWidth = 1216.dp,
            headerActions = headerActions,
            summaryCards = {},
            showSummaryCards = false,
            bannerAfterToolbar = true,
            banner = {
                val filters = subjectFilterLabels(state.filters)
                if (state.dialogMode != SubjectsDialogMode.EDITOR &&
                    (state.errorMessage.isNotBlank() || state.infoMessage.isNotBlank() || filters.isNotEmpty())
                ) {
                    AdminListMessageAndFilters(
                        message = state.errorMessage.ifBlank { state.infoMessage },
                        onDismissMessage = { onEvent(SubjectsUiEvent.ClearMessages) },
                        chips = filters,
                        onClearAll = { onEvent(SubjectsUiEvent.FilterReset) },
                        isError = state.errorMessage.isNotBlank(),
                        onRemoveChip = { chip ->
                                val next = when {
                                    chip.startsWith("Code:") -> state.filters.copy(subjectCode = "")
                                    chip.startsWith("Name:") -> state.filters.copy(subjectName = "")
                                    chip.startsWith("Category:") -> state.filters.copy(category = "")
                                    chip.startsWith("Status:") -> state.filters.copy(status = "")
                                    chip.startsWith("Created:") -> state.filters.copy(createdFrom = "", createdTo = "")
                                    else -> state.filters
                                }
                                onEvent(SubjectsUiEvent.FilterApplied(next))
                        },
                        filterBackgroundColor = Color(0xFFF2F7FF),
                        filterBorderColor = SubjectsBorder,
                        filterContentColor = SubjectsNavy,
                        filterAccentColor = SubjectsBlue,
                        messageLeadingIcon = painterResource(Res.drawable.info_circle),
                        messageBackgroundColor = if (state.errorMessage.isNotBlank()) SubjectsRedSoft else Color(0xFFEFF4FF),
                        messageBorderColor = if (state.errorMessage.isNotBlank()) SubjectsRedSoft else SubjectsBlueSoft,
                        messageContentColor = if (state.errorMessage.isNotBlank()) SubjectsRed else SubjectsBlue,
                        messageAccentColor = if (state.errorMessage.isNotBlank()) SubjectsRed else SubjectsBlue
                    )
                }
            },
            toolbar = {
                AdminListToolbar(
                    searchQuery = state.searchQuery,
                    onSearchQueryChanged = { onEvent(SubjectsUiEvent.SearchChanged(it)) },
                    searchPlaceholder = "Search by code or name...",
                    searchWidth = 332.dp,
                    surfaceColor = SubjectsSurface,
                    borderColor = SubjectsBorder,
                    searchIconTint = SubjectsMuted,
                    searchTextColor = SubjectsNavy,
                    searchCursorColor = SubjectsBlue,
                    inlineContent = {
                        AppLabeledDropdownField(
                            label = "",
                            value = state.filters.category.trim().ifBlank { "All categories" },
                                    options = state.categoryOptions,
                            onSelected = { onEvent(SubjectsUiEvent.CategoryChanged(it)) },
                            modifier = Modifier.width(190.dp),
                            allOptionLabel = "All categories"
                        )
                    }
                ) {
                    AdminListToolbarButton(
                        label = "Filters",
                        icon = painterResource(Res.drawable.filter),
                        onClick = { onEvent(SubjectsUiEvent.FilterClicked) },
                        width = 118.dp,
                        borderColor = SubjectsBorder,
                        contentColor = SubjectsBlue,
                        iconSize = 18.dp,
                        surfaceColor = SubjectsSurface
                    )
                    AdminListToolbarButton(
                        label = "Clear",
                        icon = painterResource(Res.drawable.close_x),
                        onClick = { onEvent(SubjectsUiEvent.FilterReset) },
                        width = 100.dp,
                        borderColor = SubjectsBorder,
                        contentColor = SubjectsBlue,
                        iconSize = 18.dp,
                        surfaceColor = SubjectsSurface
                    )
                }
            },
            table = { modifier ->
                AdminListTableCard(
                    modifier = modifier,
                    minContentWidth = 760.dp,
                    surfaceColor = SubjectsSurface,
                    borderColor = SubjectsBorder,
                    header = {
                        SubjectsHeaderRow(
                            sortField = state.sortField,
                            sortAscending = state.sortAscending,
                            onSortChanged = { onEvent(SubjectsUiEvent.SortChanged(it)) }
                        )
                    },
                    body = {
                        if (state.isLoading) {
                            AdminListTableShimmer(
                                columns = subjectsTableShimmerColumns(),
                                rowCount = state.paging.pageSize.coerceAtLeast(5),
                                surfaceColor = SubjectsSurface,
                                borderColor = SubjectsBorder
                            )
                        } else {
                            SubjectsTableBody(
                                items = pageItems,
                                openActionMenuSubjectId = openActionMenuSubjectId,
                                onToggleActionMenu = { subjectId ->
                                    openActionMenuSubjectId = if (openActionMenuSubjectId == subjectId) null else subjectId
                                },
                                onCloseActionMenu = { subjectId ->
                                    if (openActionMenuSubjectId == subjectId) {
                                        openActionMenuSubjectId = null
                                    }
                                },
                                onViewSubject = {
                                    openActionMenuSubjectId = null
                                    onEvent(SubjectsUiEvent.ViewClicked(it))
                                },
                                onEditSubject = {
                                    openActionMenuSubjectId = null
                                    onEvent(SubjectsUiEvent.EditClicked(it))
                                },
                                onDeleteSubject = {
                                    openActionMenuSubjectId = null
                                    onEvent(SubjectsUiEvent.DeleteClicked(it))
                                }
                            )
                        }
                    },
                    footer = {
                        AdminListFooter(
                            pageText = visibleRangeText(pageWindow.startIndex, pageWindow.endIndex, pageWindow.totalCount),
                            pageIndex = state.paging.pageIndex,
                            pageCount = pageWindow.pageCount,
                            onPageChanged = { onEvent(SubjectsUiEvent.PageChanged(it)) },
                            pageSize = state.paging.pageSize,
                            rowsPerPageExpanded = rowsPerPageExpanded,
                            onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
                            onRowsPerPageDismiss = { rowsPerPageExpanded = false },
                            onPageSizeChanged = {
                                onEvent(SubjectsUiEvent.PageSizeChanged(it))
                                rowsPerPageExpanded = false
                            },
                            surfaceColor = SubjectsSurface,
                            borderColor = SubjectsBorder,
                            primaryColor = SubjectsBlue,
                            navyColor = SubjectsNavy,
                            mutedColor = SubjectsMuted,
                            footerColor = SubjectsMuted
                        )
                    }
                )
            },
            bottomAction = {
                AdminListToolbarButton(
                    label = "Add Subject",
                    icon = painterResource(Res.drawable.plus),
                    onClick = { onEvent(SubjectsUiEvent.AddClicked) },
                    width = 164.dp,
                    borderColor = SubjectsBlue,
                    surfaceColor = SubjectsBlue,
                    contentColor = Color.White,
                    iconSize = 18.dp
                )
            }
        )

        when (state.dialogMode) {
            SubjectsDialogMode.FILTER -> SubjectFilterDialog(
                draft = state.filterDraft,
                categoryOptions = state.categoryOptions,
                onDismiss = { onEvent(SubjectsUiEvent.FilterDismissed) },
                onReset = { onEvent(SubjectsUiEvent.FilterReset) },
                onApply = { onEvent(SubjectsUiEvent.FilterApplied(it)) }
            )
            SubjectsDialogMode.EDITOR -> SubjectEditorDialog(
                draft = state.editorDraft,
                isEditing = state.editorDraft.id.isNotBlank(),
                isSaving = state.isSaving,
                apiError = state.apiErrorMessage,
                categoryOptions = state.categoryOptions,
                onDismiss = { if (!state.isSaving) onEvent(SubjectsUiEvent.DialogDismissed) },
                onSave = { onEvent(SubjectsUiEvent.EditorSaveRequested) },
                onCodeChanged = { onEvent(SubjectsUiEvent.EditorCodeChanged(it)) },
                onNameChanged = { onEvent(SubjectsUiEvent.EditorNameChanged(it)) },
                onCategoryChanged = { onEvent(SubjectsUiEvent.EditorCategoryChanged(it)) }
            )
            SubjectsDialogMode.DETAILS -> {
                val subject = pageItems.firstOrNull { it.id == state.activeSubjectId }
                if (subject != null) {
                    SubjectDetailsDialog(
                        subject = subject,
                        onDismiss = { onEvent(SubjectsUiEvent.DialogDismissed) }
                    )
                }
            }
            SubjectsDialogMode.DELETE_CONFIRM -> {
                val subject = pageItems.firstOrNull { it.id == state.activeSubjectId }
                if (subject != null) {
                    AppConfirmationDialog(
                        onDismiss = { if (!state.isSaving) onEvent(SubjectsUiEvent.DialogDismissed) },
                        title = "Delete Subject",
                        subtitle = "This subject will be deactivated and removed from the active list.",
                        bodyText = "Delete ${subject.code} - ${subject.name}?",
                        confirmLabel = "Delete",
                        onConfirm = { onEvent(SubjectsUiEvent.DeleteConfirmed) },
                        tone = AppConfirmationDialogTone.Danger,
                        confirmEnabled = !state.isSaving &&
                            !state.isAssignmentsLoading &&
                            state.assignmentsErrorMessage.isBlank(),
                        dismissEnabled = !state.isSaving,
                        content = {
                            when {
                                state.isAssignmentsLoading -> {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.Center
                                    ) {
                                        CircularProgressIndicator(
                                            modifier = Modifier.size(22.dp),
                                            color = SubjectsBlue,
                                            strokeWidth = 2.dp
                                        )
                                    }
                                }
                                state.assignmentsErrorMessage.isNotBlank() -> {
                                    Text(
                                        text = state.assignmentsErrorMessage,
                                        color = SubjectsRed,
                                        fontSize = 12.sp,
                                        modifier = Modifier.fillMaxWidth()
                                    )
                                }
                                state.assignedClasses.isNotEmpty() -> {
                                    LazyColumn(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .heightIn(max = 220.dp),
                                        verticalArrangement = Arrangement.spacedBy(8.dp),
                                        userScrollEnabled = true
                                    ) {
                                        item {
                                            Text(
                                                text = "This subject is assigned to the following classes:",
                                                color = SubjectsRed,
                                                fontSize = 13.sp,
                                                fontWeight = FontWeight.SemiBold,
                                                modifier = Modifier.fillMaxWidth()
                                            )
                                        }
                                        items(
                                            items = state.assignedClasses,
                                            key = { it.displayName }
                                        ) { assignment ->
                                            Column(modifier = Modifier.fillMaxWidth()) {
                                                Text(
                                                    text = assignment.displayName,
                                                    color = SubjectsNavy,
                                                    fontSize = 14.sp,
                                                    modifier = Modifier.fillMaxWidth()
                                                )
                                                HorizontalDivider(color = SubjectsBorder)
                                            }
                                        }
                                    }
                                }
                                else -> {
                                    Text(
                                        text = "This subject is not assigned to any class.",
                                        color = SubjectsMuted,
                                        fontSize = 13.sp,
                                        modifier = Modifier.fillMaxWidth()
                                    )
                                }
                            }
                        }
                    )
                }
            }
            SubjectsDialogMode.NONE -> Unit
        }
    }
}
